import { defineStore } from 'pinia';

type ThemeMode = 'light' | 'dark' | 'system';

interface IAppStore {
  themeMode: ThemeMode
  systemDark: boolean
  sidebarExpand: boolean
  wrapperWidth: number | string
  wrapperLeftOffset: number | string
  navWidth: number | string
}

const LIGHT = 'light';
const DARK = 'dark';
const SYSTEM = 'system';
const THEME_KEY = 'themeMode';
const EXPAND = 280;
const SHRINKED = 72;
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

let systemThemeQuery: MediaQueryList | null = null;

const resolveTheme = (mode: ThemeMode, systemDark: boolean): 'light' | 'dark' =>
  mode === SYSTEM ? (systemDark ? DARK : LIGHT) : mode;

const isStoredTheme = (value: string | null): value is ThemeMode =>
  value === LIGHT || value === DARK || value === SYSTEM;

export const useAppStore = defineStore('app', {
  state: () => <IAppStore>({
    themeMode: LIGHT,
    systemDark:
      typeof window !== 'undefined' && !!window.matchMedia?.(DARK_SCHEME_QUERY).matches,
    sidebarExpand: true,
    wrapperWidth: 0,
    wrapperLeftOffset: 0,
    navWidth: '100%'
  }),
  getters: {
    theme: (state) => state.themeMode,
    isDark: (state) =>
      state.themeMode === DARK || (state.themeMode === SYSTEM && state.systemDark),
    sidebarExpanded: (state) => state.sidebarExpand,
  },
  actions: {
    toggleSidebar() {
      this.sidebarExpand = !this.sidebarExpand;
      if (window.innerWidth > 1024) {
        this.initWrapper();
      }
    },
    initWrapper() {
      if (window.innerWidth > 1024) {
        if (this.sidebarExpand) {
          this.wrapperWidth = EXPAND;
          this.wrapperLeftOffset = EXPAND;
        } else {
          this.wrapperWidth = SHRINKED;
          this.wrapperLeftOffset = SHRINKED;
        }
        this.navWidth = `calc(100% - ${this.wrapperWidth}px)`
      } else {
        this.navWidth = '100%';
        this.sidebarExpand = false;
        this.wrapperWidth = '100%';
        this.wrapperLeftOffset = '100%';
      }
    },
    initTheme() {
      const cache = localStorage.getItem(THEME_KEY);
      if (isStoredTheme(cache)) {
        this.themeMode = cache;
      }
      this.watchSystemTheme();
      window.addEventListener('resize', this.initWrapper);
      this.applyTheme();
      this.initWrapper();
    },
    watchSystemTheme() {
      if (systemThemeQuery) return;
      systemThemeQuery = window.matchMedia(DARK_SCHEME_QUERY);
      this.systemDark = systemThemeQuery.matches;
      systemThemeQuery.addEventListener('change', this.onSystemThemeChange);
    },
    onSystemThemeChange(event: MediaQueryListEvent) {
      this.systemDark = event.matches;
      if (this.themeMode === SYSTEM) {
        this.applyTheme();
      }
    },
    toggleTheme() {
      const resolved = resolveTheme(this.themeMode, this.systemDark);
      this.themeMode = resolved === LIGHT ? DARK : LIGHT;
      this.applyTheme();
      localStorage.setItem(THEME_KEY, this.themeMode);
    },
    appUnmount() {
      window.removeEventListener('resize', this.initWrapper);
    },
    applyTheme() {
      const resolved = resolveTheme(this.themeMode, this.systemDark);
      document.documentElement.classList.remove(LIGHT, DARK);
      document.body.classList.remove(LIGHT, DARK);
      document.documentElement.classList.add(resolved);
      document.body.classList.add(resolved);
    },
  },
});
